import { Box, Button, Collapse, HStack, Spinner, Text, VStack, useDisclosure } from '@chakra-ui/react';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { theme } from '@resistapp/client/components/shared/theme';
import { MaybeApiError } from '@resistapp/client/components/states/error';
import { LoadingView } from '@resistapp/client/components/states/loading';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { usePatchShippingNotificationSettings, useShippingNotificationSettings } from '@resistapp/client/hooks/api';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { isResistomapAdminUser } from '@resistapp/client/utils/access';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { AdminShipmentsSection } from './admin-shipments-view';

export function AdminShippingView() {
  const { user, userLoading } = useUser();

  if (userLoading) {
    return <LoadingView />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isResistomapAdminUser(user)) {
    return <Navigate to="/admin/sample-shipping" replace />;
  }

  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar title="Shipping" $extraWide={true} />
      <VStack align="stretch" spacing={6} padding={theme.spacing[4]}>
        <ShippingNotificationSettings />
        <AdminShipmentsSection scope="admin" />
      </VStack>
    </div>
  );
}

function ShippingNotificationSettings() {
  const { data, isPending, error } = useShippingNotificationSettings();
  const patchSettings = usePatchShippingNotificationSettings();
  const queryClient = useQueryClient();
  const showToast = useToastWithoutDebounce();
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });

  return (
    <Box bg="white" borderRadius={6} padding={theme.spacing[4]} boxShadow="sm">
      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between" align="center">
          <Text fontSize="lg" fontWeight={600}>
            Email and Slack notifications
          </Text>
          <Button size="sm" variant="ghost" onClick={onToggle} aria-expanded={isOpen}>
            {isOpen ? 'Hide' : 'Show'}
          </Button>
        </HStack>
        <Collapse in={isOpen} animateOpacity>
          <VStack align="stretch" spacing={3} pt={1}>
            <Text color="gray.600">
              Resistomap admins on this list receive an email and a personal message from the Resistomap Platform Slack
              app whenever a new shipping request is submitted. Slack accounts are matched by email address.
            </Text>
            {isPending ? (
              <HStack spacing={2}>
                <Spinner size="sm" />
                <Text color="gray.600">Loading notification settings…</Text>
              </HStack>
            ) : error ? (
              <MaybeApiError error={error} />
            ) : (
              <>
                <Button
                  alignSelf="flex-start"
                  colorScheme={data.subscribed ? 'red' : 'blue'}
                  variant={data.subscribed ? 'outline' : 'solid'}
                  textTransform="none"
                  isLoading={patchSettings.isPending}
                  onClick={() => {
                    void (async () => {
                      try {
                        const updatedSettings = await patchSettings.mutateAsync({ subscribed: !data.subscribed });
                        queryClient.setQueryData(['/admin/shipping/notification-settings'], updatedSettings);
                        showToast({
                          title: updatedSettings.subscribed
                            ? 'Shipping notifications enabled'
                            : 'Shipping notifications disabled',
                          status: 'success',
                          duration: 5000,
                        });
                      } catch (patchError) {
                        showToast({
                          title: 'Notification settings not saved',
                          description: patchError instanceof Error ? patchError.message : 'Please try again.',
                          status: 'error',
                          duration: 5000,
                        });
                      }
                    })();
                  }}
                >
                  {data.subscribed ? 'Remove me from the list' : 'Add me to the list'}
                </Button>
                <Box>
                  <Text fontSize="sm" fontWeight={600} mb={1}>
                    Current recipients
                  </Text>
                  {data.subscribers.length === 0 ? (
                    <Text fontSize="sm" color="gray.600">
                      No admins are subscribed yet.
                    </Text>
                  ) : (
                    <VStack align="stretch" spacing={1}>
                      {data.subscribers.map(subscriber => (
                        <Text key={subscriber.id} fontSize="sm" color="gray.700">
                          {subscriber.firstName} {subscriber.lastName} ({subscriber.email})
                        </Text>
                      ))}
                    </VStack>
                  )}
                </Box>
              </>
            )}
          </VStack>
        </Collapse>
      </VStack>
    </Box>
  );
}
